// Travel time to work ("Restid till jobbet"), shared by both views, the filter drawer and the listing popup.
// The workplace, travel mode and time limit are saved in localStorage (`commute_v1`) and shared through a
// small store. The filter drawer edits a draft (useCommuteDraft) that becomes the applied value, which the
// maps, cards and popup read, when the drawer's search button is pressed (applyCommuteDraft). The times come from the backend's /travel/* endpoints (travel.py), which ask the self-hosted
// routing engines (Valhalla for car/bike/walk, MOTIS for public transport). Every trip arrives by 08:30 on
// a weekday. While the engines don't answer (/travel/status), the feature stays hidden.
import { useEffect, useState } from 'react';
import { SwedenSearchProvider } from './searchProvider.js';
import { apiFetch } from './edgePayloads.js';

const API_BASE = String(process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu').replace(/\/+$/, '');
const STORAGE_KEY = 'commute_v1';

export const COMMUTE_MODES = ['transit', 'bike', 'car', 'walk'];
export const COMMUTE_MODE_LABELS = {
    transit: { sv: 'Kollektivt', en: 'Transit', short: { sv: 'kollektivt', en: 'transit' } },
    bike: { sv: 'Cykel', en: 'Bike', short: { sv: 'cykel', en: 'bike' } },
    car: { sv: 'Bil', en: 'Car', short: { sv: 'bil', en: 'car' } },
    walk: { sv: 'Gång', en: 'Walk', short: { sv: 'gång', en: 'walk' } },
};
// Line icons per mode (24×24, stroked), for the React icon (CommuteFilter.js) and the map's HTML cards.
export const COMMUTE_MODE_ICON_PATHS = {
    transit: 'M7 4h10a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM5 11h14M8 20l1.5-3M16 20l-1.5-3',
    bike: 'M6 17a3 3 0 1 0 0-.01M18 17a3 3 0 1 0 0-.01M6 17l4-7h5l3 7M10 10L9 7H7',
    car: 'M5 17h14M6 17l1.5-5h9l1.5 5M7 17v2M17 17v2',
    walk: 'M13 4a1.5 1.5 0 1 0 0 .01M10 21l2-6 3 3v3M9 12l3-3 3 2 2 2M12 9l-1 6',
};

// The limit slider: null = no limit (times still show on homes).
export const COMMUTE_MAX_STEPS = [null, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 90];

const DEFAULT_STATE = { place: null, mode: 'transit', maxMinutes: null };

const readState = () => {
    try {
        const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        if (!saved || typeof saved !== 'object') return DEFAULT_STATE;
        const place = saved.place && Number.isFinite(saved.place.lat) && Number.isFinite(saved.place.lng)
            ? { label: String(saved.place.label || ''), lat: saved.place.lat, lng: saved.place.lng }
            : null;
        return {
            place,
            mode: COMMUTE_MODES.includes(saved.mode) ? saved.mode : 'transit',
            maxMinutes: COMMUTE_MAX_STEPS.includes(saved.maxMinutes) ? saved.maxMinutes : null,
        };
    } catch (error) {
        return DEFAULT_STATE;
    }
};

let state = readState();
const listeners = new Set();

export const getCommute = () => state;

export const setCommute = (patch) => {
    state = { ...state, ...patch };
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
        // Private mode or full storage: kept for this page only.
    }
    listeners.forEach((listener) => listener(state));
};

export const useCommute = () => {
    const [value, setValue] = useState(state);
    useEffect(() => {
        listeners.add(setValue);
        setValue(state);
        return () => listeners.delete(setValue);
    }, []);
    return value;
};

// The drawer's edits, not applied until its search button is pressed.
let draft = state;
const draftListeners = new Set();
const sameCommute = (a, b) => a.mode === b.mode
    && a.maxMinutes === b.maxMinutes
    && (a.place === b.place || (a.place && b.place && a.place.lat === b.place.lat && a.place.lng === b.place.lng));

export const getCommuteDraft = () => draft;

export const setCommuteDraft = (patch) => {
    draft = { ...draft, ...patch };
    draftListeners.forEach((listener) => listener(draft));
};

export const useCommuteDraft = () => {
    const [value, setValue] = useState(draft);
    useEffect(() => {
        draftListeners.add(setValue);
        setValue(draft);
        return () => draftListeners.delete(setValue);
    }, []);
    return value;
};

export const isCommuteDraftPending = () => !sameCommute(draft, state);

export const applyCommuteDraft = () => {
    if (isCommuteDraftPending()) setCommute(draft);
};

const postJson = async (path, body, signal) => {
    const response = await apiFetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
    });
    if (!response.ok) {
        const error = new Error(`travel request failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }
    return response.json();
};

// Which routing engines answer, asked once per page load: transit (MOTIS) and road (Valhalla: bike, car, walk).
// Each works on its own: with one down the feature stays, its modes greyed out (the owner, 2026-11-01, when
// Valhalla was down and the whole Restid section had gone).
let statusPromise = null;
const travelEngines = () => {
    if (!statusPromise) {
        statusPromise = apiFetch(`${API_BASE}/travel/status`, { headers: { Accept: 'application/json' } })
            .then((response) => (response.ok ? response.json() : null))
            .then((body) => ({ transit: Boolean(body?.motis), road: Boolean(body?.valhalla) }))
            .catch(() => ({ transit: false, road: false }));
    }
    return statusPromise;
};

// Whether a mode can be worked out with the engines that answer.
export const modeWorks = (engines, mode) => Boolean(engines && (mode === 'transit' ? engines.transit : engines.road));

// { transit, road }, or null until the status has come back.
export const useTravelEngines = () => {
    const [engines, setEngines] = useState(null);
    useEffect(() => {
        let active = true;
        travelEngines().then((value) => {
            if (active) setEngines(value);
        });
        return () => {
            active = false;
        };
    }, []);
    return engines;
};

// true while at least one engine answers (the feature shows), false when none does, null until known.
export const useTravelAvailable = () => {
    const engines = useTravelEngines();
    return engines ? (engines.transit || engines.road) : null;
};

// Address search for the workplace (Nominatim, Sweden, short labels).
let provider = null;
export const searchWorkplace = async (query) => {
    if (!provider) provider = new SwedenSearchProvider();
    const results = await provider.search({ query });
    return results.slice(0, 5).map((result) => ({ label: result.label, lat: result.y, lng: result.x }));
};

const reachCache = new Map();
const reachKey = (place, mode, maxMinutes) => (
    place && maxMinutes ? `${place.lat.toFixed(4)},${place.lng.toFixed(4)}|${mode}|${maxMinutes}` : ''
);

// The RegSO areas within the time limit, as a Set (null when no limit is set or nothing has loaded yet).
// Both views treat areas outside it like areas outside the area-score filter. `value`: another setting than
// the applied one (the drawer counts its draft's areas).
export const useCommuteReach = (value = null) => {
    const applied = useCommute();
    const commute = value || applied;
    const engines = useTravelEngines();
    // A mode whose engine is down has no limit to apply.
    const key = modeWorks(engines, commute.mode) ? reachKey(commute.place, commute.mode, commute.maxMinutes) : '';
    const [reach, setReach] = useState(() => (key ? reachCache.get(key) || null : null));
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!key) {
            setReach(null);
            setLoading(false);
            return undefined;
        }
        if (reachCache.has(key)) {
            setReach(reachCache.get(key));
            setLoading(false);
            return undefined;
        }
        const controller = new AbortController();
        setLoading(true);
        postJson('/travel/reach', {
            lat: commute.place.lat, lng: commute.place.lng, mode: commute.mode, minutes: commute.maxMinutes,
        }, controller.signal)
            .then((body) => {
                const set = new Set((body.regsoIds || []).map(String));
                reachCache.set(key, set);
                setReach(set);
                setLoading(false);
            })
            .catch((error) => {
                if (controller.signal.aborted) return;
                setReach(null);
                setLoading(false);
                console.warn('Restid: reach failed', error);
            });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return { reach, loading };
};

const timesCache = new Map();
const timeRequests = new Map();

// One listing's minutes to work in the chosen mode, for the map's HTML cards (no hook): { minutes, mode },
// or null without a workplace or engines. Shares the cards' cache.
export const fetchCommuteMinutes = async (listing) => {
    const { place, mode } = state;
    const lat = Number(listing?.lat);
    const lng = Number(listing?.lng);
    if (!place || !listing?.id || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (!modeWorks(await travelEngines(), mode)) return null;
    const id = String(listing.id);
    const key = `${place.lat.toFixed(4)},${place.lng.toFixed(4)}|${mode}|${id}`;
    if (!timesCache.has(key) && !timeRequests.has(key)) {
        timeRequests.set(key, postJson('/travel/times', { lat: place.lat, lng: place.lng, modes: [mode], points: [{ id, lat, lng }] })
            .then((body) => {
                timesCache.set(key, body.times?.[id]?.[mode] ?? null);
            })
            .finally(() => timeRequests.delete(key)));
    }
    if (timeRequests.has(key)) await timeRequests.get(key);
    return { minutes: timesCache.get(key) ?? null, mode };
};

// Minutes to work for the given listings in the chosen mode: { [listing id]: minutes | null }.
export const useCommuteTimes = (listings) => {
    const commute = useCommute();
    const engines = useTravelEngines();
    const works = modeWorks(engines, commute.mode);
    const [times, setTimes] = useState({});
    const place = works ? commute.place : null;
    const ids = (listings || [])
        .filter((listing) => Number.isFinite(listing?.lat) && Number.isFinite(listing?.lng))
        .map((listing) => String(listing.id));
    const idsKey = ids.join('|');
    useEffect(() => {
        if (!place || !ids.length) {
            setTimes({});
            return undefined;
        }
        const base = `${place.lat.toFixed(4)},${place.lng.toFixed(4)}|${commute.mode}|`;
        const known = {};
        const missing = [];
        (listings || []).forEach((listing) => {
            if (!Number.isFinite(listing?.lat) || !Number.isFinite(listing?.lng)) return;
            const cacheKey = base + listing.id;
            if (timesCache.has(cacheKey)) known[listing.id] = timesCache.get(cacheKey);
            else missing.push(listing);
        });
        setTimes(known);
        if (!missing.length) return undefined;
        const controller = new AbortController();
        postJson('/travel/times', {
            lat: place.lat,
            lng: place.lng,
            modes: [commute.mode],
            points: missing.slice(0, 100).map((listing) => ({ id: String(listing.id), lat: listing.lat, lng: listing.lng })),
        }, controller.signal)
            .then((body) => {
                const next = { ...known };
                Object.entries(body.times || {}).forEach(([id, byMode]) => {
                    const minutes = byMode?.[commute.mode];
                    timesCache.set(base + id, minutes ?? null);
                    next[id] = minutes ?? null;
                });
                setTimes(next);
            })
            .catch((error) => {
                if (!controller.signal.aborted) console.warn('Restid: times failed', error);
            });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [place?.lat, place?.lng, commute.mode, idsKey]);
    return { times, mode: commute.mode, active: Boolean(place) };
};

const routeCache = new Map();
const routeRequests = new Map();

// One request per trip, shared by everything showing it (the popup's key number and its Till jobbet row).
const fetchRoute = (key, body) => {
    if (!routeRequests.has(key)) {
        routeRequests.set(key, postJson('/travel/route', body)
            .then((route) => {
                routeCache.set(key, route);
                return route;
            })
            .finally(() => routeRequests.delete(key)));
    }
    return routeRequests.get(key);
};

// The trip from a home to work: { transit: {minutes, transfers, legs}, bike: {minutes, km}, car, walk }.
export const useCommuteRoute = (listing) => {
    const commute = useCommute();
    const place = commute.place;
    const lat = Number(listing?.lat);
    const lng = Number(listing?.lng);
    const key = place && Number.isFinite(lat) && Number.isFinite(lng)
        ? `${lat.toFixed(5)},${lng.toFixed(5)}|${place.lat.toFixed(4)},${place.lng.toFixed(4)}`
        : '';
    const [route, setRoute] = useState(() => (key ? routeCache.get(key) || null : null));
    const [status, setStatus] = useState('idle');
    useEffect(() => {
        if (!key) {
            setRoute(null);
            setStatus('idle');
            return undefined;
        }
        if (routeCache.has(key)) {
            setRoute(routeCache.get(key));
            setStatus('done');
            return undefined;
        }
        let live = true;
        setStatus('loading');
        fetchRoute(key, { from: { lat, lng }, to: { lat: place.lat, lng: place.lng } })
            .then((body) => {
                if (!live) return;
                setRoute(body);
                setStatus('done');
            })
            .catch(() => {
                if (live) setStatus('error');
            });
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return { route, status, place, mode: commute.mode };
};

export const commuteModeLabel = (mode, swedish, short = false) => {
    const labels = COMMUTE_MODE_LABELS[mode] || COMMUTE_MODE_LABELS.transit;
    return short ? labels.short[swedish ? 'sv' : 'en'] : labels[swedish ? 'sv' : 'en'];
};
